/* eslint-disable max-lines */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { tap } from 'rxjs/operators';

import { GeneralConstants } from 'app/constants/general-constants';
import { Comment, CrisRequest, ListOptions, makeCrisRequest, RestfulModel, SeamRequestFilter } from 'app/model';
import { Brand } from 'app/model/cris/brand';
import { BrandAttributes } from 'app/model/cris/brand-attributes';
import { Store } from 'app/model/cris/store';
import { KnpPaginationData } from 'app/model/knp-pagination-data';
import { Partner } from 'app/model/mcp/partner';
import { PartnerMap } from 'app/model/mcp/partner-map';
import { attributeService } from 'app/services/brand/attribute.service';
import { brandService } from 'app/services/brand.service';
import { crisCommentService } from 'app/services/comment.service';
import { crisRequestService } from 'app/services/cris-request.service';
import { partnerMapService } from 'app/services/partner-map.service';
import { partnerService } from 'app/services/partner.service';
import { storeService } from 'app/services/store.service';
import { AppThunk, ObservableThunk, RootState } from 'app/store';
import { ModelWithoutId } from 'app/type';

interface CrisState {
    /**
     * The request loaded based on path's :id parameter under route /cris/request
     */
    currentRequest?: CrisRequest;
    comments: Comment[];
    createFormData: ModelWithoutId<CrisRequest>;

    /**
     * The partner loaded based on path's :id parameter under route /cris/partners
     */
    currentPartner?: Partner;
    partnerSearchTerm: string;
    partnerSearchResults: Partner[];

    /**
     * Brand screen
     */
    currentBrand?: Brand;
    possibleAttributes?: string[];
    prodDataStatus: boolean;

    possibleStoreAttributes?: string[];
}

const initialState: CrisState = {
    comments: [],
    createFormData: makeCrisRequest(),
    partnerSearchTerm: '',
    prodDataStatus: false,
    partnerSearchResults: [],
};

const crisSlice = createSlice({
    name: 'cris',
    initialState,
    reducers: {
        // cris request screens
        setCurrentCrisRequest: (state, action: PayloadAction<CrisRequest | undefined>) => {
            state.currentRequest = action.payload;
        },
        setCrisComments: (state, action: PayloadAction<Comment[]>) => {
            state.comments = action.payload;
        },
        setCrisFormData: (state, action: PayloadAction<ModelWithoutId<CrisRequest>>) => {
            state.createFormData = action.payload;
        },
        // partner screens
        setCurrentCrisPartner: (state, action: PayloadAction<Partner | undefined>) => {
            state.currentPartner = action.payload;
        },
        setCrisPartnerSearchTerm: (state, action: PayloadAction<string>) => {
            state.partnerSearchTerm = action.payload;
        },
        setCrisPartnerSearchResults: (state, action: PayloadAction<Partner[]>) => {
            state.partnerSearchResults = action.payload;
        },
        // brand screens
        setCurrentBrand: (state, action: PayloadAction<Brand | undefined>) => {
            state.currentBrand = action.payload;
        },
        setPossibleAttributes: (state, action: PayloadAction<string[]>) => {
            state.possibleAttributes = action.payload;
        },
        setPossibleStoreAttributes: (state, action: PayloadAction<string[]>) => {
            state.possibleStoreAttributes = action.payload;
        },
        setProdDataStatus: (state, action: PayloadAction<boolean>) => {
            state.prodDataStatus = action.payload;
        },
    },
});

// main reducer.
export const CrisSlice = crisSlice.reducer;

// selectors
export const selectCurrentRequest = (state: RootState): CrisRequest | undefined => state.cris.currentRequest;

export const selectCurrentPartner = (state: RootState): Partner | undefined => state.cris.currentPartner;

export const selectCrisComments = (state: RootState): Comment[] => state.cris.comments;

export const selectCrisFormData = (state: RootState): ModelWithoutId<CrisRequest> => state.cris.createFormData;

export const selectPartnerSearchTerm = (state: RootState): string => state.cris.partnerSearchTerm;

export const selectPartnerSearchResults = (state: RootState): Partner[] => state.cris.partnerSearchResults;

export const selectCurrentBrand = (state: RootState): Brand | undefined => state.cris.currentBrand;

export const selectPossibleAttributes = (state: RootState): string[] | undefined => state.cris.possibleAttributes;

export const selectPossibleStoreAttributes = (state: RootState): string[] | undefined =>
    state.cris.possibleStoreAttributes;

export const selectProdDataStatus = (state: RootState): boolean => state.cris.prodDataStatus;

// actions
export const {
    setCurrentCrisRequest,
    setCrisComments,
    setCrisFormData,
    setCurrentCrisPartner,
    setCrisPartnerSearchTerm,
    setCrisPartnerSearchResults,
    setCurrentBrand,
    setPossibleAttributes,
    setPossibleStoreAttributes,
    setProdDataStatus,
} = crisSlice.actions;

// async actions
export const loadCrisComments = (request: RestfulModel): ObservableThunk<Comment[]> => {
    return () => crisCommentService.listAll({}, request);
};

export const addCrisComment =
    (comment: string, request: CrisRequest): ObservableThunk<Comment> =>
    (dispatch, state) =>
        crisCommentService.save({ comment }, request).pipe(
            tap((com) => {
                dispatch(setCrisComments([...selectCrisComments(state()), com]));
            }),
        );

export const findCrisPartners = (): AppThunk => (dispatch, state) => {
    const name = state().cris.partnerSearchTerm;
    if (!name) {
        // do not search blanks
        return;
    }
    partnerService
        .listAll({
            pageSize: GeneralConstants.PAGE_SIZE_HUGE,
            filters: { name },
        })
        .subscribe((partners) => {
            dispatch(setCrisPartnerSearchResults(partners));
        });
};

export const loadCrisPartner =
    (id: string): AppThunk =>
    (dispatch, state) => {
        if (state().cris.currentPartner?.id === id) {
            // already loaded do nothin
            return;
        }

        partnerService.get(id).subscribe((partner) => {
            dispatch(setCurrentCrisPartner(partner));
        });
    };

export const linkCrisPartnerStore =
    (partner: Partner, store: Store): AppThunk =>
    (dispatch, state) => {
        partnerService.linkStore(partner, store).subscribe((map) => {
            const currentPartner = state().cris.currentPartner;
            if (currentPartner?.id !== partner.id) {
                // already looking at another partner so we are done
                return;
            }

            // need to add the record from our mapping in state
            dispatch(
                setCurrentCrisPartner({
                    ...currentPartner,
                    storeMaps: [map, ...currentPartner.storeMaps],
                }),
            );
        });
    };

export const deleteCrisPartnerMap =
    (map: PartnerMap, partner: Partner): AppThunk =>
    (dispatch, state) => {
        partnerMapService.delete(map, partner).subscribe(() => {
            const currentPartner = state().cris.currentPartner;
            if (currentPartner?.id !== partner.id) {
                // already looking at another partner so we are done
                return;
            }

            // need to remove the record from our mapping in state
            dispatch(
                setCurrentCrisPartner({
                    ...currentPartner,
                    storeMaps: currentPartner.storeMaps.filter((item) => map.id !== item.id),
                }),
            );
        });
    };

export const patchCrisPartnerMap =
    (map: Pick<PartnerMap, 'id'>, partner: Partner, field: string, value: unknown): AppThunk =>
    (dispatch, state) => {
        partnerMapService.patch(map, { [field]: value }, partner).subscribe((newMap) => {
            const currentPartner = state().cris.currentPartner;
            if (currentPartner?.id !== partner.id) {
                // already looking at another partner so we are done
                return;
            }

            // need to update the record from our mapping in state
            dispatch(
                setCurrentCrisPartner({
                    ...currentPartner,
                    storeMaps: currentPartner.storeMaps.map((item) => (map.id === item.id ? newMap : item)),
                }),
            );
        });
    };

export const loadCrisRequest =
    (id: string): ObservableThunk<CrisRequest> =>
    () =>
        crisRequestService.get(id);

export const saveCrisRequest =
    (data: ModelWithoutId<CrisRequest>, skipCris?: boolean): ObservableThunk<CrisRequest> =>
    () => {
        if (skipCris) {
            return crisRequestService.saveForSeamOnly(data);
        }

        return crisRequestService.save(data);
    };

export const fetchCrisRequests =
    (filter?: SeamRequestFilter): ObservableThunk<KnpPaginationData<CrisRequest>> =>
    () => {
        const opts: ListOptions = { pageSize: 200 };

        if (!filter) {
            return crisRequestService.list(opts);
        }

        // initializes to empty object if it is undefined
        opts.filters = {
            ...opts.filters,
        };

        if (filter.status) {
            opts.filters.status = 'string' === typeof filter.status ? filter.status : filter.status.join(',');
        }

        if (filter.my) {
            opts.filters.my = filter.my;
        }

        if (filter.assignee) {
            opts.filters.assignee = filter.assignee.id;
        }

        if (filter.term) {
            opts.filters.term = filter.term;
        }

        return crisRequestService.list(opts);
    };

export const executeCrisRequest =
    (data: CrisRequest): ObservableThunk<CrisRequest> =>
    () =>
        crisRequestService.execute(data);

export const loadBrand =
    (id: string): ObservableThunk<Brand> =>
    (dispatch) => {
        dispatch(setCurrentBrand());

        return brandService.get(id).pipe(
            tap((brand) => {
                dispatch(setCurrentBrand(brand));
            }),
        );
    };

export const prepareAttributes = (): AppThunk => (dispatch, state) => {
    if (undefined !== state().cris.possibleAttributes) {
        // already loaded
        return;
    }

    brandService.loadPossibleAttributes().subscribe((attrs) => dispatch(setPossibleAttributes(attrs)));
};

export const prepareStoreAttributes = (): AppThunk => (dispatch, state) => {
    if (undefined !== state().cris.possibleStoreAttributes) {
        // already loaded
        return;
    }

    storeService.loadPossibleAttributes().subscribe((attrs) => dispatch(setPossibleStoreAttributes(attrs)));
};

export const saveNewAttribute =
    (attr: ModelWithoutId<BrandAttributes>, attrBrand: Brand): ObservableThunk<BrandAttributes> =>
    (dispatch, state) =>
        attributeService.save(attr, attrBrand).pipe(
            tap((attribute) => {
                const brand = state().cris.currentBrand;
                if (!brand) {
                    return;
                }
                if (brand.id !== attrBrand.id) {
                    // switch to new brand
                    return;
                }
                dispatch(
                    setCurrentBrand({
                        ...brand,
                        attributes: [attribute, ...(brand.attributes ?? [])],
                    }),
                );
            }),
        );
